<template>
  <div id="app">
    <!-- Barre de navigation -->
    <NavbarComponent v-if="!isAdminInterface"/>
    <div v-if="showPublicPopup" class="site-popup-backdrop" :class="popupDisplayMode">
      <aside
        class="site-popup"
        :class="[activePopup.template, popupDisplayMode]"
        :style="popupStyle"
        role="dialog"
        :aria-modal="popupDisplayMode === 'fullscreen' ? 'true' : 'false'"
        aria-labelledby="site-popup-title"
      >
        <button type="button" class="site-popup__close" :aria-label="isEnglish ? 'Close' : 'Fermer'" @click="closePopup">×</button>
        <img v-if="activePopup.image_url" class="site-popup__image" :src="activePopup.image_url" alt="" />
        <p class="site-popup__label">{{ popupTemplateLabel }}</p>
        <h2 id="site-popup-title">{{ popupTitle }}</h2>
        <p>{{ popupMessage }}</p>
        <a
          v-if="popupButtonText && activePopup.button_url"
          class="site-popup__button"
          :style="popupButtonStyle"
          :href="localizedPopupButtonUrl"
        >
          {{ popupButtonText }}
        </a>
      </aside>
    </div>
    
    <!-- Contenu principal -->
    <main>
      <router-view :getPage="getPage" :adminInterface="adminInterface" />
    </main>
    <!-- Pied de page -->
    <FooterComponent v-if="!isAdminInterface"/>
  </div>
</template>

<script>
import NavbarComponent from "@/components/Navbar.vue";
import FooterComponent from "@/components/Footer.vue";
import axios from 'axios';
import { apiUrl } from '@/config/api';

export default {
  name: "App",
  components: {
    NavbarComponent,
    FooterComponent
  },
  data () {
    return {
      isAdminInterface: false,
      activePopup: null,
      popupDismissed: false
    }
  },
  computed: {
    isEnglish() {
      return this.$route.path.startsWith('/en');
    },
    popupStorageKey() {
      if (!this.activePopup) return 'nyenyezia-popup-dismissed';
      return [
        'nyenyezia-popup-dismissed',
        this.activePopup.template,
        this.activePopup.language,
        this.activePopup.title_fr,
        this.activePopup.title_en,
        this.activePopup.start_at,
        this.activePopup.end_at,
        this.activePopup.display_mode
      ].filter(Boolean).join(':');
    },
    popupDisplayMode() {
      return this.activePopup?.display_mode === 'fullscreen' ? 'fullscreen' : 'modal';
    },
    popupColorDefaults() {
      if (this.popupDisplayMode === 'fullscreen') {
        return {
          background_color: '#001041',
          text_color: '#ffffff',
          accent_color: '#c39446',
          button_background_color: '#c39446',
          button_text_color: '#001041'
        };
      }

      return {
        background_color: '#ffffff',
        text_color: '#172033',
        accent_color: '#c39446',
        button_background_color: '#007acc',
        button_text_color: '#ffffff'
      };
    },
    popupColors() {
      return {
        background: this.activePopup?.background_color || this.popupColorDefaults.background_color,
        text: this.activePopup?.text_color || this.popupColorDefaults.text_color,
        accent: this.activePopup?.accent_color || this.popupColorDefaults.accent_color,
        buttonBackground: this.activePopup?.button_background_color || this.popupColorDefaults.button_background_color,
        buttonText: this.activePopup?.button_text_color || this.popupColorDefaults.button_text_color
      };
    },
    popupStyle() {
      return {
        backgroundColor: this.popupColors.background,
        color: this.popupColors.text,
        borderColor: this.popupColors.accent,
        borderLeftColor: this.popupColors.accent
      };
    },
    popupButtonStyle() {
      return {
        backgroundColor: this.popupColors.buttonBackground,
        color: this.popupColors.buttonText
      };
    },
    popupAllowedForLanguage() {
      if (!this.activePopup) return false;
      if (this.activePopup.language === 'both') return true;
      return this.isEnglish ? this.activePopup.language === 'en' : this.activePopup.language === 'fr';
    },
    popupTitle() {
      if (!this.activePopup) return '';
      return this.isEnglish
        ? (this.activePopup.title_en || this.activePopup.title_fr)
        : this.activePopup.title_fr;
    },
    popupMessage() {
      if (!this.activePopup) return '';
      return this.isEnglish
        ? (this.activePopup.message_en || this.activePopup.message_fr)
        : this.activePopup.message_fr;
    },
    popupButtonText() {
      if (!this.activePopup) return '';
      return this.isEnglish
        ? (this.activePopup.button_text_en || this.activePopup.button_text_fr)
        : this.activePopup.button_text_fr;
    },
    popupTemplateLabel() {
      const labels = {
        closure: this.isEnglish ? 'Temporary closure' : 'Fermeture temporaire',
        promotion: this.isEnglish ? 'Special update' : 'Information spéciale',
        schedule: this.isEnglish ? 'Schedule change' : 'Changement d’horaire',
        general: this.isEnglish ? 'Notice' : 'Annonce'
      };
      return labels[this.activePopup?.template] || labels.general;
    },
    localizedPopupButtonUrl() {
      const url = this.activePopup?.button_url || '/';
      if (/^https?:\/\//i.test(url) || !url.startsWith('/')) return url;
      if (!this.isEnglish) return url;
      if (url === '/') return '/en/';
      return url.startsWith('/en/') ? url : '/en' + url;
    },
    showPublicPopup() {
      return Boolean(
        !this.isAdminInterface
        && !this.$route.path.includes('/dashboard')
        && this.activePopup
        && this.activePopup.enabled
        && !this.popupDismissed
        && this.popupAllowedForLanguage
        && this.popupIsInDateRange(this.activePopup)
        && this.popupTitle
        && this.popupMessage
      );
    }
  },
  mounted() {
    this.loadPublicPopup();
  },
  methods: {
    async getPage (name) {
      var config = {
        method: 'get',
        url: apiUrl('/page?name=' + encodeURIComponent(name)),
        headers: { 
          'Content-Type': 'application/json'
        }
      };

      return await axios(config)
      .then(function (response) {
        const config = response?.data?.config || null;
        return JSON.parse(config);
      })
      .catch(function (error) {
        return error;
      });

    },
    adminInterface (value) {
      this.isAdminInterface = value;
    },
    popupIsInDateRange(popup) {
      const now = new Date();
      if (popup.start_at) {
        const start = new Date(popup.start_at + 'T00:00:00');
        if (!Number.isNaN(start.getTime()) && now < start) return false;
      }
      if (popup.end_at) {
        const end = new Date(popup.end_at + 'T23:59:59');
        if (!Number.isNaN(end.getTime()) && now > end) return false;
      }
      return true;
    },
    async loadPublicPopup() {
      if (this.$route.path.includes('/dashboard')) return;
      try {
        const page = await this.getPage('home');
        const popup = page?.site_popup || page?.popups || null;
        this.activePopup = popup && typeof popup === 'object' ? popup : null;
        this.popupDismissed = this.isPopupDismissed();
      } catch (error) {
        this.activePopup = null;
      }
    },
    isPopupDismissed() {
      try {
        return window.sessionStorage.getItem(this.popupStorageKey) === '1';
      } catch (error) {
        return false;
      }
    },
    closePopup() {
      this.popupDismissed = true;
      try {
        window.sessionStorage.setItem(this.popupStorageKey, '1');
      } catch (error) {
        // Ignore storage failures; the close button should still work.
      }
    }
  },
  watch: {
    $route(to, from) {
      this.isAdminInterface = false;
      this.loadPublicPopup();
      // Perform some action when the route changes
      console.log("Route changed", to, from);

      // Prevent default scroll-to-top behavior
      const scrollPosition = 0;
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);
    },
  },
};
</script>

<style>
/* Styles globaux */
body {
  margin: 0;
  font-family: "Arial", sans-serif;
  color: #333;
  background-color: #f9f9f9;
}

main {
  min-height: calc(100vh - 120px); /* Ajuste la hauteur selon la taille du header et footer */

}
.page {
  margin-top: 65px;
}

.form-actions {
    position: fixed;
    width: 100%;
    right: 0;
    bottom: 0;
    text-align: right;
    padding: 10PX;
    background: #999;
    display: none;
  }

.form-actions.active {
    display: initial;
  }

.site-popup-backdrop {
  bottom: 20px;
  left: 20px;
  pointer-events: none;
  position: fixed;
  right: 20px;
  z-index: 9999;
}

.site-popup-backdrop.fullscreen {
  align-items: center;
  background: rgba(0, 16, 65, 0.72);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  padding: 24px;
  pointer-events: auto;
  right: 0;
  top: 0;
}

.site-popup {
  background: #fff;
  border: 1px solid #dbe3ef;
  border-left: 5px solid #007acc;
  border-radius: 8px;
  box-shadow: 0 18px 46px rgba(0, 16, 65, 0.2);
  color: #172033;
  margin-left: auto;
  max-width: 440px;
  padding: 20px;
  pointer-events: auto;
  position: relative;
}

.site-popup.fullscreen {
  align-items: center;
  border-left-width: 1px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0;
  max-height: calc(100vh - 48px);
  max-width: 920px;
  min-height: min(620px, calc(100vh - 48px));
  overflow-y: auto;
  padding: clamp(28px, 6vw, 64px);
  text-align: center;
  width: min(100%, 920px);
}

.site-popup.closure {
  border-left-color: #c39446;
}

.site-popup.promotion {
  border-left-color: #166534;
}

.site-popup.schedule {
  border-left-color: #7c3aed;
}

.site-popup__close {
  background: #eef3fb;
  border: none;
  border-radius: 50%;
  color: #001041;
  cursor: pointer;
  font-size: 1.35rem;
  height: 32px;
  line-height: 1;
  position: absolute;
  right: 12px;
  top: 12px;
  width: 32px;
}

.site-popup__label {
  color: inherit;
  font-size: 0.76rem;
  font-weight: 700;
  margin: 0 40px 8px 0;
  opacity: 0.78;
  text-transform: uppercase;
}

.site-popup h2 {
  color: inherit;
  font-size: 1.25rem;
  line-height: 1.25;
  margin: 0 40px 8px 0;
}

.site-popup p {
  color: inherit;
  line-height: 1.6;
  margin: 0 0 14px;
}

.site-popup.fullscreen .site-popup__label,
.site-popup.fullscreen h2,
.site-popup.fullscreen p {
  margin-left: auto;
  margin-right: auto;
  max-width: 700px;
}

.site-popup.fullscreen h2 {
  font-size: clamp(2rem, 5vw, 4rem);
  line-height: 1.06;
  margin-bottom: 18px;
}

.site-popup.fullscreen p {
  font-size: 1.08rem;
}

.site-popup__image {
  border-radius: 8px;
  display: block;
  margin: 0 40px 16px 0;
  max-height: 220px;
  max-width: 100%;
  object-fit: cover;
  width: 100%;
}

.site-popup.fullscreen .site-popup__image {
  margin: 0 auto 24px;
  max-height: min(34vh, 320px);
  max-width: 680px;
}

.site-popup__button {
  background: #007acc;
  border-radius: 8px;
  color: #fff;
  display: inline-block;
  font-weight: 700;
  padding: 10px 14px;
  text-decoration: none;
}

.site-popup__button:hover {
  filter: brightness(0.94);
}

@media (max-width: 640px) {
  .site-popup-backdrop {
    bottom: 12px;
    left: 12px;
    right: 12px;
  }

  .site-popup {
    max-width: none;
  }

  .site-popup.fullscreen {
    max-height: calc(100vh - 24px);
    min-height: calc(100vh - 24px);
    padding: 28px 20px;
  }

  .site-popup__image {
    margin-right: 0;
  }
}
</style>
